import { Form } from '../services/formService';
import { Job } from '../services/jobService';

/**
 * Converts a Uint8Array of bytes to a base64 string.
 * Processes in chunks to avoid call stack overflow with large files.
 * @param bytes The byte array to convert
 * @returns Promise resolving to base64 encoded string
 */
export const convertBytesToBase64 = async (bytes: Uint8Array): Promise<string> => {
  const chunkSize = 8192; // 8KB chunks
  let result = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    result += String.fromCharCode.apply(null, Array.from(chunk));
  }

  return btoa(result);
};

/**
 * Sanitizes a string for use in filenames by replacing non-alphanumeric characters with hyphens.
 * @param str The string to sanitize
 * @returns Sanitized string safe for filenames
 */
export const sanitizeForFilename = (str: string): string => {
  return str
    .replace(/[^a-zA-Z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Generates a PDF filename based on job number and form description.
 * @param job The job object (or object with jobNumber property)
 * @param form The form object (or object with description property)
 * @returns Generated filename in format: {jobNumber}-{formDescription}.pdf
 */
export const generatePdfFilename = (
  job: Pick<Job, 'jobNumber'> | null | undefined,
  form: Pick<Form, 'description'>
): string => {
  const sanitizedFormName = sanitizeForFilename(form.description);
  const sanitizedJobNumber = job?.jobNumber
    ? sanitizeForFilename(job.jobNumber)
    : 'job';
  return `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;
};

/**
 * Creates a blob URL from PDF bytes and triggers a download.
 * @param pdfBytes The PDF content as Uint8Array
 * @param filename The filename for the download
 */
export const downloadPdfBytes = (pdfBytes: Uint8Array, filename: string): void => {
  const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up URL after download
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10000);
};

/**
 * Opens a PDF in a hidden iframe and triggers the print dialog.
 * @param pdfBytes The PDF content as Uint8Array
 * @returns Promise that resolves when print dialog is opened
 */
export const printPdfBytes = (pdfBytes: Uint8Array): Promise<void> => {
  return new Promise((resolve) => {
    const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      // Give it some time to open the print dialog before cleaning up
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
        resolve();
      }, 60000);
    };
  });
};

/**
 * Opens a PDF in a new browser tab.
 * @param pdfBytes The PDF content as Uint8Array
 */
export const openPdfInNewTab = (pdfBytes: Uint8Array): void => {
  const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  window.open(url, '_blank');

  // Clean up URL after a delay
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10000);
};
